import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Brain, Save, Zap, Layers, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const SUB_ITEMS = [
  { key: 'emotional', label: 'Emotional recognition', help: 'How well did you notice emotions arising?' },
  { key: 'focus', label: 'Focus stability', help: 'How steady was your attention?' },
  { key: 'breathing', label: 'Breathing awareness', help: 'Did you notice your breath through the round?' },
  { key: 'body', label: 'Body tension awareness', help: 'Did you spot tension before it cost you a shot?' },
  { key: 'thought', label: 'Thought awareness', help: 'Did you notice negative self-talk without believing it?' },
];

const ROUND_TYPES = [
  { key: 'practice', label: 'Practice' },
  { key: 'casual', label: 'Casual' },
  { key: 'competitive', label: 'Competitive' },
  { key: 'tournament', label: 'Tournament' },
];

const ScaleRow = ({ value, onChange, onToggleNA, label, help, testid }) => {
  const isNA = value === null;
  return (
    <div className="py-3 border-b border-[#F2F0E9] last:border-0" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1A1A1A]">{label}</p>
          {help && <p className="text-xs text-[#737373] mt-0.5">{help}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isNA && (
            <span className="text-lg font-['Playfair_Display'] text-[#1A2E1D] tabular-nums">
              {value || 0}<span className="text-xs text-[#737373]"> / 10</span>
            </span>
          )}
          {isNA && (
            <span className="text-xs uppercase tracking-wider font-semibold text-[#A8A29E]">N/A</span>
          )}
          <button
            type="button"
            onClick={onToggleNA}
            className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border transition-colors ${
              isNA
                ? 'bg-[#1A2E1D] text-white border-[#1A2E1D]'
                : 'bg-white text-[#737373] border-[#E7E5E4] hover:border-[#1A2E1D]/40'
            }`}
            title={isNA ? 'Mark as relevant again' : "Mark N/A — won't count toward your average"}
            data-testid={`${testid}-na-toggle`}
          >
            N/A
          </button>
        </div>
      </div>
      <input
        type="range"
        min={1}
        max={10}
        step={1}
        value={isNA ? 5 : (value || 5)}
        disabled={isNA}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className={`w-full accent-[#2C5530] ${isNA ? 'opacity-30 cursor-not-allowed' : ''}`}
      />
      <div className="flex justify-between text-[10px] text-[#A8A29E] mt-1 px-0.5">
        <span>Low</span><span>5</span><span>High</span>
      </div>
    </div>
  );
};

const AwarenessLogPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('quick');
  const [saving, setSaving] = useState(false);
  const [overall, setOverall] = useState(7);
  const [sub, setSub] = useState({ emotional: 7, focus: 7, breathing: 7, body: 7, thought: 7 });
  const [roundType, setRoundType] = useState('');
  const [courseName, setCourseName] = useState('');
  const [score, setScore] = useState('');
  const [notable, setNotable] = useState('');
  const [reflection, setReflection] = useState('');
  const [roundDate, setRoundDate] = useState(() => new Date().toISOString().split('T')[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        mode,
        round_type: roundType || null,
        course_name: courseName.trim() || null,
        score: score === '' ? null : parseInt(score, 10),
        notable_moments: notable.trim() || null,
        reflection: reflection.trim() || null,
        round_date: roundDate,
      };
      if (mode === 'quick') payload.overall = overall;
      if (mode === 'detailed') {
        // Strip N/A (null) values — backend will compute overall from what's left
        const filtered = Object.fromEntries(
          Object.entries(sub).filter(([_, v]) => typeof v === 'number')
        );
        if (!Object.keys(filtered).length) {
          toast.error('Mark at least one indicator with a score (not N/A).');
          setSaving(false);
          return;
        }
        payload.sub_scores = filtered;
      }
      await API.post('/locker/awareness', payload);
      toast.success('Round logged. Your future self thanks you.');
      navigate('/locker');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log round.');
    } finally {
      setSaving(false);
    }
  };

  const subAvg = useMemo(() => {
    if (mode !== 'detailed') return null;
    const vals = Object.values(sub).filter((v) => typeof v === 'number');
    if (!vals.length) return 0;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }, [mode, sub]);

  const naCount = useMemo(
    () => Object.values(sub).filter((v) => v === null).length,
    [sub]
  );

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="awareness-log-page">
      {/* Top bar */}
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-3xl flex items-center gap-3">
          <Link to="/locker" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="back-to-locker">
            <ArrowLeft className="w-4 h-4" /> Locker
          </Link>
        </div>
      </div>

      <main className="container mx-auto px-6 md:px-10 py-10 max-w-3xl">
        <div className="mb-8">
          <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
            <Brain className="w-4 h-4" /> Awareness · Log a round
          </p>
          <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-2">How aware were you today?</h1>
          <p className="text-[#57534E]">Honest beats high. The point is to see — not score.</p>
        </div>

        {/* Mode toggle */}
        <div className="flex bg-white border border-[#E7E5E4] p-1 mb-6 max-w-md" role="tablist">
          <button
            type="button"
            onClick={() => setMode('quick')}
            className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors ${
              mode === 'quick' ? 'bg-[#1A2E1D] text-white' : 'text-[#57534E] hover:text-[#1A1A1A]'
            }`}
            data-testid="mode-quick"
          >
            <Zap className="w-4 h-4" /> Quick (30s)
          </button>
          <button
            type="button"
            onClick={() => setMode('detailed')}
            className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors ${
              mode === 'detailed' ? 'bg-[#1A2E1D] text-white' : 'text-[#57534E] hover:text-[#1A1A1A]'
            }`}
            data-testid="mode-detailed"
          >
            <Layers className="w-4 h-4" /> Detailed (2m)
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Score block */}
          <div className="bg-white border border-[#E7E5E4] p-6">
            {mode === 'quick' ? (
              <ScaleRow
                value={overall}
                onChange={setOverall}
                label="Overall Awareness"
                help="Across the whole round — emotions, focus, body, thoughts."
                testid="scale-overall"
              />
            ) : (
              <>
                <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-3">
                  5 indicators · averages to your Awareness score
                  {naCount > 0 && <span className="ml-2 text-[#BC562B] normal-case tracking-normal">({naCount} N/A — not counted)</span>}
                </p>
                {SUB_ITEMS.map((it) => (
                  <ScaleRow
                    key={it.key}
                    value={sub[it.key]}
                    onChange={(v) => setSub({ ...sub, [it.key]: v })}
                    onToggleNA={() => setSub({ ...sub, [it.key]: sub[it.key] === null ? 7 : null })}
                    label={it.label}
                    help={it.help}
                    testid={`scale-${it.key}`}
                  />
                ))}
                <p className="text-sm text-[#1A1A1A] mt-4 pt-4 border-t border-[#F2F0E9] flex items-center justify-between">
                  <span className="font-semibold">Overall Awareness (avg of scored)</span>
                  <span className="font-['Playfair_Display'] text-2xl text-[#1A2E1D] tabular-nums">{subAvg}<span className="text-xs text-[#737373]"> / 10</span></span>
                </p>
              </>
            )}
          </div>

          {/* Context */}
          <div className="bg-white border border-[#E7E5E4] p-6 space-y-5">
            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Round type</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ROUND_TYPES.map((rt) => (
                  <button
                    type="button"
                    key={rt.key}
                    onClick={() => setRoundType(roundType === rt.key ? '' : rt.key)}
                    className={`text-xs font-semibold uppercase tracking-wider px-3 py-2.5 border transition-colors ${
                      roundType === rt.key
                        ? 'bg-[#1A2E1D] text-white border-[#1A2E1D]'
                        : 'bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#1A2E1D]/40'
                    }`}
                    data-testid={`round-type-${rt.key}`}
                  >
                    {rt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" /> Round date
                </label>
                <input
                  type="date"
                  value={roundDate}
                  onChange={(e) => setRoundDate(e.target.value)}
                  className="w-full"
                  data-testid="round-date"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Course (optional)</label>
                <input
                  type="text"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="Bristol Golf Club"
                  className="w-full"
                  data-testid="course-name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Score (optional)</label>
              <input
                type="number"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                placeholder="Leave blank to keep it mental-only"
                className="w-full max-w-[200px]"
                data-testid="round-score"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Notable moments</label>
              <textarea
                value={notable}
                onChange={(e) => setNotable(e.target.value)}
                rows={2}
                placeholder="One or two moments that stood out — good or bad."
                className="w-full"
                data-testid="notable-moments"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Reflection</label>
              <textarea
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                rows={4}
                placeholder="What did you notice today? When did Awareness wobble? What will you take into the next round?"
                className="w-full"
                data-testid="reflection-text"
              />
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <Link
              to="/locker"
              className="flex-1 inline-flex items-center justify-center px-6 py-3 border border-[#E7E5E4] text-[#57534E] hover:bg-[#F2F0E9] transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors disabled:opacity-50"
              data-testid="submit-awareness-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Log round'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AwarenessLogPage;
